import { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "./CallFeaturesModal.css";
import * as BoxesActions from "../../src/actions/EpikBoxes";
import { connect } from "react-redux";
import BulkSelectPortsModal from "./BulkSelectionModal";
import { toast } from "react-toastify";
import InfoPopupCallFeature from '../../src/components/callFeatureInfoPop'
import {TOOLTIPS_TEXT} from "../../src/lib/constants"
import Toggle from '../../src/components/Toggle/index';
import { isEscene } from '../../src/lib/utils';
const DIGITS_ONLY = /\D+/g;
const sanitizeDigits = (s) => String(s || "").replace(DIGITS_ONLY, "");
const isValidPhone = (s, minLen = 9) => sanitizeDigits(s).length >= minLen;

function PhoneInput({ value, onChange, placeholder = "Enter number", minLen = 9 }) {
  const v = sanitizeDigits(value);
  return (
    <input
      className="cf-input"
      inputMode="numeric"
      pattern="[0-9]*"
      minLength={minLen}
      value={v}
      placeholder={placeholder}
      onChange={(e) => onChange(sanitizeDigits(e.target.value))}
    />
  );
}

function buildTabsAndGrid(boxDetails, oobData) {
  const collected = [];
  const collectFromDevice = (deviceData, sourceType) => {
    (deviceData?.Obis || []).forEach((obi) => {
      const p1 = obi?.port1;
      if (p1 && p1.assignedNumber) {
        const num = p1.boxPortNumber ?? collected.length + 1;
        collected.push({
          label: `${num}`,
          source: sourceType,
          boxId: deviceData._id,
          obiId: obi._id,
          boxPortNumber: num,
          macAddress: obi.macAddress,        
        });
      }
      const p2 = obi?.port2;
      if (p2 && p2.assignedNumber) {
        const num = p2.boxPortNumber ?? collected.length + 1;
        collected.push({
          label: `${num}`,
          source: sourceType,
          boxId: deviceData._id,
          obiId: obi._id,
          boxPortNumber: num,
          macAddress: obi.macAddress,           
        });
      }
    });
    (oobData?.Obis || []).forEach((obi) => {
      if (obi?.port1 && obi.port1.assignedNumber) {
        collected.push({
          label: `${obi.port1.boxPortNumber}`,
          source: "oob",
          boxId: boxDetails._id,
          obiId: obi._id,
          boxPortNumber: obi.port1.boxPortNumber,
          macAddress: obi.macAddress,         
        });
      }
      if (obi?.port2 && obi.port2.assignedNumber) {
        collected.push({
          label: `${obi.port2.boxPortNumber}`,
          source: "oob",
          boxId: boxDetails._id,
          obiId: obi._id,
          boxPortNumber: obi.port2.boxPortNumber,
          macAddress: obi.macAddress,          
        });
      }
    });
  };
  collectFromDevice(boxDetails, "box");
  const headerTabs = ["Device Level", ...collected.slice(0, 8).map((p) => p.label)];
  const gridPorts = collected.length > 8 ? collected.slice(8).map((p) => p.label) : [];
  return { headerTabs, gridPorts, ports: collected };
}

const SECONDS_OPTS = Array.from({ length: 55 }, (_, i) => i + 6);
const cx = (...c) => c.filter(Boolean).join(" ");

function Pill({ active, onClick, children }) {
  return (
    <button className={cx("cf-pill", active && "active")} onClick={onClick}>
      {children}
    </button>
  );
}

function SecondsDropdown({ value, onChange }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const MENU_MAX_HEIGHT = 240;
  const updatePos = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const openUp = r.bottom + MENU_MAX_HEIGHT > window.innerHeight && r.top > MENU_MAX_HEIGHT;
    const top = window.scrollY + (openUp ? r.top - MENU_MAX_HEIGHT - 6 : r.bottom + 6);
    setPos({ top, left: window.scrollX + r.left, width: r.width });
  };
  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
    const onWin = () => updatePos();
    const onClick = (e) => {
      if (!btnRef.current) return;
      if (e.target === btnRef.current || btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin);
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin);
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const menu = open
    ? createPortal(
        <div className="cf-dd-menu" style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: MENU_MAX_HEIGHT }}>
          <ul className="cf-dd-list" role="listbox" aria-label="Seconds">
            {SECONDS_OPTS.map((n) => (
              <li
                key={n}
                role="option"
                aria-selected={n === value}
                className={cx("cf-dd-item", n === value && "selected")}
                onClick={() => {
                  onChange(n);
                  setOpen(false);
                }}
              >
                {n}
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )
    : null;
  return (
    <>
      <button type="button" ref={btnRef} className="cf-dd-btn" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {value}
        <span className="cf-dd-caret" aria-hidden>▾</span>
      </button>
      {menu}
    </>
  );
}

const defaultPortState = () => ({
  busyOn: false,
  busyExpand: false,
  busyRoute: "voicemail",
  busyPhone: "",
  naOn: false,
  naExpand: false,
  naSeconds: 6,
  naRoute: "voicemail",
  naPhone: "",
  alwaysOn: false,
  alwaysExpand: false,
  alwaysRoute: "voicemail",
  alwaysPhone: "",
  remoteOn: false,
  dndOn: false,
  threeWayOn: false,
});

function CallFeaturesModal({
  open,
  onClose,
  boxDetails,
  getSalveboxOob,
  updateDeviceFeatures,
  updatePortFeature,
  bulkUpdateCallFeaturePorts,
}) {
  const [activeTab, setActiveTab] = useState("Device Level");
  const [selectedGridPort, setSelectedGridPort] = useState(null);
  const [acr, setAcr] = useState(false);
  const [block, setBlock] = useState(false);
  const [blockExpand, setBlockExpand] = useState(false);
  const [blockInput, setBlockInput] = useState("");
  const [blockList, setBlockList] = useState([]);
  const [savingBlock, setSavingBlock] = useState(false);
  const [portState, setPortState] = useState({});
  const [saving, setSaving] = useState({});
  const [oobError, setOobError] = useState("");
  const [oobData, setOobData] = useState(null);
  const token = localStorage.getItem("jwtToken") || null;
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [isEsceneDevice, setIsEsceneDevice] = useState(false);

  const { headerTabs: topTabs, gridPorts, ports: portsMeta } = useMemo(
    () => buildTabsAndGrid(boxDetails, oobData),
    [boxDetails, oobData]
  );

  const portMetaByLabel = useMemo(() => {
    const m = new Map();
    for (const p of portsMeta) m.set(p.label, p);
    return m;
  }, [portsMeta]);

  useEffect(() => {
    const valid = activeTab === "Device Level" || topTabs.includes(activeTab) || /^Port \d+$/.test(activeTab);
    if (!valid) setActiveTab("Device Level");
  }, [topTabs, activeTab]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setOobError("");
        const bid = boxDetails?.slaveBoxes?.[0]?._id;
        if (!bid) return;
        const data = await getSalveboxOob(token, bid);
        setOobData(data);
      } catch (e) {
        setOobError(e?.message || String(e));
      }
    })();
  }, [open, boxDetails, getSalveboxOob, token]);

  useEffect(() => {
    if (!open || !boxDetails) return;
    setAcr(!!boxDetails.callRejection);
    const blockingEnabled = !!(boxDetails.callBlockingSettings && boxDetails.callBlockingSettings.enabled);
    setBlock(blockingEnabled);
    setBlockExpand(blockingEnabled);
    if (boxDetails.callBlockingSettings && Array.isArray(boxDetails.callBlockingSettings.numbers)) {
      const numbers = boxDetails.callBlockingSettings.numbers
        .map((n) => (typeof n === "string" ? n : n.number))
        .filter(Boolean);
      setBlockList(numbers);
    } else {
      setBlockList([]);
    }
  }, [open, boxDetails]);

  useEffect(() => {
    if (!oobData && !boxDetails) return;
    const newPortState = {};
    setIsEsceneDevice(false);
    const initPortFromData = (portData, label) => {
      if (!portData) return;
      newPortState[label] = {
        busyOn: !!(portData.callForwardBusy && portData.callForwardBusy.enabled),
        busyExpand: !!(portData.callForwardBusy && portData.callForwardBusy.enabled),
        busyRoute: portData.callForwardBusy?.route || "voicemail",
        busyPhone: portData.callForwardBusy?.phoneNumber || "",
        naOn: !!(portData.callForwardNoAnswer && portData.callForwardNoAnswer.enabled),
        naExpand: !!(portData.callForwardNoAnswer && portData.callForwardNoAnswer.enabled),
        naSeconds: portData.callForwardNoAnswer?.ringSeconds || 6,
        naRoute: portData.callForwardNoAnswer?.route || "voicemail",
        naPhone: portData.callForwardNoAnswer?.phoneNumber || "",
        alwaysOn: !!(portData.callForwardAlways && portData.callForwardAlways.enabled),
        alwaysExpand: !!(portData.callForwardAlways && portData.callForwardAlways.enabled),
        alwaysRoute: portData.callForwardAlways?.route || "voicemail",
        alwaysPhone: portData.callForwardAlways?.phoneNumber || "",
        remoteOn: !!portData.remoteCallForwarding,
        dndOn: !!portData.doNotDisturb,
        threeWayOn: !!portData.conferenceCall,
      };
    };
    (boxDetails?.Obis || []).forEach((obi) => {
      if (isEscene(obi?.macAddress)) {
        setIsEsceneDevice(true);
      }
      if (obi?.port1 && obi.port1.assignedNumber) {
        const label = String(obi.port1.boxPortNumber);
        initPortFromData(obi.port1, label);
      }
      if (obi?.port2 && obi.port2.assignedNumber) {
        const label = String(obi.port2.boxPortNumber);
        initPortFromData(obi.port2, label);
      }
    });
    (oobData?.Obis || []).forEach((obi) => {
      if (isEscene(obi?.macAddress)) {
        setIsEsceneDevice(true);
      }
      if (obi?.port1 && obi.port1.assignedNumber) {
        const label = String(obi.port1.boxPortNumber);
        initPortFromData(obi.port1, label);
      }
      if (obi?.port2 && obi.port2.assignedNumber) {
        const label = String(obi.port2.boxPortNumber);
        initPortFromData(obi.port2, label);
      }
    });
    setPortState(newPortState);
  }, [boxDetails, oobData]);

  if (!open) return null;

  const isPortView = activeTab.startsWith("Port ");
  const currentPort = isPortView ? activeTab : null;
  const normalizePortLabel = (lbl) => (lbl ? String(lbl).replace(/^Port\s+/i, "") : null);
  const currentLabel = normalizePortLabel(currentPort);

  const selectTab = (tab) => {
    setActiveTab(tab);
    if (tab === "Device Level" || /^Port \d+$/.test(tab)) setSelectedGridPort(null);
  };
  const selectGridPort = (p) => {
    setActiveTab(p);
    setSelectedGridPort(p);
  };

  const getP = (p) => portState[p] ?? defaultPortState();
  const setP = (p, updater) =>
    setPortState((s) => {
      const cur = s[p] ?? defaultPortState();
      const next = typeof updater === "function" ? updater(cur) : updater;
      return { ...s, [p]: next };
    });

  const addBlockNumber = () => {
    const num = sanitizeDigits(blockInput.trim());
    if (!num) return;
    if (!isValidPhone(num)) {
      toast.error("Number must be at least 9 digits");
      return;
    }
    if (blockList.includes(num)) {
      setBlockInput("");
      return;
    }
    setBlockList((prev) => [...prev, num]);
    setBlockInput("");
  };

  const removeBlockNumber = (num) => setBlockList((prev) => prev.filter((n) => n !== num));
  const handleBlockInputChange = (e) => setBlockInput(sanitizeDigits(e.target.value));

  const handleBlockInputKeyDown = (e) => {
    const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"];
    if (allowed.includes(e.key)) {
      if (e.key === "Enter") {
        e.preventDefault();
        addBlockNumber();
      }
      return;
    }
    if (/^[0-9]$/.test(e.key)) return;
    e.preventDefault();
  };

  const persistDisableCallBlocking = async () => {
    await updateDeviceFeatures(token, boxDetails._id, {
      callBlocking: false,
      callBlockingSettings: { enabled: false, numbers: [] },
    });
  };

  const handleSaveCallBlocking = async () => {
    try {
      setSavingBlock(true);
      const body = {
        callBlocking: !!block,
        callBlockingSettings: {
          enabled: !!block,
          numbers: block ? blockList.map((n) => ({ number: String(n) })) : [],
        },
      };
      await updateDeviceFeatures(token, boxDetails?._id, body);
      toast.success("Call Blocking settings saved");
    } catch (err) {
      toast.error(err?.message || "Failed to save Call Blocking");
    } finally {
      setSavingBlock(false);
    }
  };

  const buildBusyPayload = (s) => {
    if (!s.busyOn) return { callForwardBusy: { enabled: false, route: "voicemail", phoneNumber: "" } };
    const route = s.busyRoute === "phone" ? "phone" : "voicemail";
    const phoneNumber = route === "phone" ? sanitizeDigits(s.busyPhone) : "";
    return { callForwardBusy: { enabled: true, route, phoneNumber } };
  };

  const buildNoAnswerPayload = (s) => {
    if (!s.naOn) return { callForwardNoAnswer: { enabled: false, ringSeconds: 6, route: "voicemail", phoneNumber: "" } };
    const route = s.naRoute === "phone" ? "phone" : "voicemail";
    const phoneNumber = route === "phone" ? sanitizeDigits(s.naPhone) : "";
    const ringSeconds = Number(s.naSeconds || 6);
    return { callForwardNoAnswer: { enabled: true, ringSeconds, route, phoneNumber } };
  };

  const buildAlwaysPayload = (s) => {
    if (!s.alwaysOn) return { callForwardAlways: { enabled: false, route: "voicemail", phoneNumber: "" } };
    const route = s.alwaysRoute === "phone" ? "phone" : "voicemail";
    const phoneNumber = route === "phone" ? sanitizeDigits(s.alwaysPhone) : "";
    return { callForwardAlways: { enabled: true, route, phoneNumber } };
  };

  const withSaving = async (key, fn) => {
    if (saving[key]) return;
    try {
      setSaving((m) => ({ ...m, [key]: true }));
      await fn();
    } catch (err) {
      toast.error(err?.message || "Operation failed");
    } finally {
      setSaving((m) => ({ ...m, [key]: false }));
    }
  };

  const handleSaveBusy = async () => {
    const meta = portMetaByLabel.get(currentPort);
    if (!meta) throw new Error("Port data not found");
    const s = getP(currentPort);
    if (s.busyOn && s.busyRoute === "phone" && !isValidPhone(s.busyPhone)) {
      toast.error("Busy forwarding number must be at least 9 digits");
      return;
    }
    const payload = buildBusyPayload(s);
    await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, payload);
    toast.success(`Call Forward Busy saved for ${currentPort}`);
  };

  const handleSaveNoAnswer = async () => {
    const meta = portMetaByLabel.get(currentPort);
    if (!meta) throw new Error("Port data not found");
    const s = getP(currentPort);
    if (s.naOn && s.naRoute === "phone" && !isValidPhone(s.naPhone)) {
      toast.error("No-Answer forwarding number must be at least 9 digits");
      return;
    }
    const payload = buildNoAnswerPayload(s);
    await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, payload);
    toast.success(`Call Forward No-Answer saved for ${currentPort}`);
  };

  const handleSaveAlways = async () => {
    const meta = portMetaByLabel.get(currentPort);
    if (!meta) throw new Error("Port data not found");
    const s = getP(currentPort);
    if (s.alwaysOn && s.alwaysRoute === "phone" && !isValidPhone(s.alwaysPhone)) {
      toast.error("Always forwarding number must be at least 9 digits");
      return;
    }
    const payload = buildAlwaysPayload(s);
    await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, payload);
    toast.success(`Call Forward Always saved for ${currentPort}`);
  };

  const saveSimpleToggle = async (label, key, value) => {
    const meta = portMetaByLabel.get(label);
    if (!meta) throw new Error("Port data not found");
    const resUpdate = await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, { [key]: !!value });
    const nice = key === "doNotDisturb" ? "Do Not Disturb" : "3 Way Calling";
    toast.success(`${nice} ${value ? "enabled" : "disabled"} for ${label}`);
    return resUpdate;
  };

  const canSaveBusy =
    isPortView &&
    (!getP(currentPort).busyOn ||
      getP(currentPort).busyRoute === "voicemail" ||
      (getP(currentPort).busyRoute === "phone" && isValidPhone(getP(currentPort).busyPhone)));

  const canSaveNA =
    isPortView &&
    (!getP(currentPort).naOn ||
      getP(currentPort).naRoute === "voicemail" ||
      (getP(currentPort).naRoute === "phone" && isValidPhone(getP(currentPort).naPhone)));

  const canSaveAlways =
    isPortView &&
    (!getP(currentPort).alwaysOn ||
      getP(currentPort).alwaysRoute === "voicemail" ||
      (getP(currentPort).alwaysRoute === "phone" &&
        isValidPhone(getP(currentPort).alwaysPhone)));
  // === Warning modal handlers for 3-way calling ===

  return (
    <>
    <div className="cf-root">
      <div className="cf-backdrop" onClick={onClose} />
      <div className="cf-card">
        <div className="cf-topbar">
          <div className="cf-header-row">
            <h2 className="cf-title">
              Call Features <span className="cf-dash">-</span>{" "}
              <span className="cf-sub">{boxDetails?.serialNumber} - {boxDetails?.displayName}</span>
            </h2>
            <div className="cf-head-actions">
              <button
                className="cf-btn bulk"
                onClick={() => {
                    setShowBulkModal(true);
                }}
              >
                Bulk Select Ports
              </button>
            </div>
          </div>
          {oobError && <div className="cf-error">{oobError}</div>}
          <div className="cf-tabs">
            {topTabs.map((t) => (
              <Pill key={t} active={activeTab === t} onClick={() => selectTab(t)}>{t}</Pill>
            ))}
          </div>
          {gridPorts.length > 0 && (
            <div className="cf-portsWrap">
              <div className="cf-portsRow">
                {gridPorts.map((p) => (
                  <button key={p} className={cx("cf-portCell", selectedGridPort === p && "selected")} onClick={() => selectGridPort(p)}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="cf-content">
          {!isPortView && (
            <div className="cf-device-wrap">
              <div className="cf-section-title big">Device Level Call Features</div>

              <div className="cf-threecol">
                <div className="cf-threecol-left">Anonymous Call Rejection  <InfoPopupCallFeature id="callRejection" text={TOOLTIPS_TEXT.ANONYMOUS_CALL_REJECTION}  />
                </div>
                <div className="cf-threecol-mid">
                  <Toggle
                    value={acr}
                    onToggle={async (v) => {
                      const next = !v;           // v = current, next = toggled
                      setAcr(next);
                      try {
                        await updateDeviceFeatures(token, boxDetails._id, { callRejection: next });
                        toast.success(`Anonymous Call Rejection ${next ? "enabled" : "disabled"}`);
                      } catch (e) {
                        toast.error(e?.message || "Failed to update ACR");
                      }
                    }}
                  />
                </div>
                <div className="cf-threecol-right">
                  <span><b>Enable</b> *77</span>
                  <span><b>Disable</b> *87</span>
                </div>
              </div>

              <div className="cf-threecol">
                <div className="cf-threecol-left">Call Blocking <InfoPopupCallFeature id="callBlocking" text={TOOLTIPS_TEXT.CALL_BLOCKING}/></div>
                <div className="cf-threecol-mid">
                  <Toggle
                    value={block}
                    onToggle={async (v) => {
                      const next = !v;
                      setBlock(next);
                      setBlockExpand(next);
                      if (next) {
                        toast.info("Call Blocking enabled in UI. Click Save to apply.");
                      } else {
                        setBlockInput("");
                        setBlockList([]);
                        try {
                          await persistDisableCallBlocking();
                          toast.success("Call Blocking disabled");
                        } catch (e) {
                          toast.error(e?.message || "Failed to disable Call Blocking");
                        }
                      }
                    }}
                  />
                </div>
                <div className="cf-threecol-right">
                  <span><b>Enable</b> *60</span>
                  <span><b>Disable</b> *80</span>
                </div>
              </div>

              {blockExpand && (
                <div className="cf-blocklist-wrap">
                  <div className="cf-blocklist-inputRow">
                    <input
                      className="cf-input cf-blocklist-input"
                      value={blockInput}
                      onChange={handleBlockInputChange}
                      onKeyDown={handleBlockInputKeyDown}
                      placeholder="Type number and press Enter"
                    />
                    <button className="cf-btn cf-add-btn" type="button" onClick={addBlockNumber} disabled={!isValidPhone(blockInput)}>
                      <span className="cf-add-btn-plus">＋</span>
                      <span>Add</span>
                    </button>
                  </div>
                  <div className="cf-blocklist-hint">Type a value and press <b>enter</b> to add to the list</div>
                  {blockList.length > 0 && (
                    <div className="cf-blocklist-tags">
                      {blockList.map((num) => (
                        <span key={num} className="cf-tag-pill">
                          <span className="cf-tag-text">{num}</span>
                          <button className="cf-tag-remove" onClick={() => removeBlockNumber(num)} type="button" aria-label={`Remove ${num}`}>−</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="cf-blocklist-saveRow">
                    <button className="cf-btn inline-save" disabled={savingBlock} onClick={handleSaveCallBlocking}>
                      {savingBlock ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

            {isPortView && (
              <div className="cf-port-wrap">
                <div className="cf-section">
                  {/* 3 Way Calling row */}
                  {isEsceneDevice && (
                  <div className="cf-row3">
                    <div className="cf-row3-left">
                      <span className="cf-spacer" />
                      <span className="cf-section-title">3 Way Calling</span>
                    </div>
                    <div className="cf-row3-mid">
                      <Toggle
                        value={getP(currentPort).threeWayOn}
                        onToggle={async (v) => {
                          const next = !v;
                          setP(currentPort, (s) => ({
                            ...s,
                            threeWayOn: next,
                          }));
                          try {
                            await saveSimpleToggle(currentPort, "conferenceCall", next);
                          } catch (e) {
                            toast.error(
                              e?.message ||
                              `Failed to ${next ? "enable" : "disable"} 3 Way Calling`
                            );
                            setP(currentPort, (s) => ({ ...s, threeWayOn: !next }));
                          }
                        }}
                      />
                    </div>
                    <div className="cf-row3-right">
                      <span>
                        <b>Enable</b>
                      </span>
                    </div>
                  </div>
                  )}
                  {/* 3 Way Calling row */}
                  <div className="cf-section-title">{currentPort} Call Forwarding</div>                <div className="cf-row3">
                  <div className="cf-row3-left">
                    <span className={cx("cf-caret", getP(currentPort).busyExpand && "open")} onClick={() => setP(currentPort, (s) => ({ ...s, busyExpand: !s.busyExpand }))} />
                    <span className="cf-label">Call Forward Busy</span>
                    <InfoPopupCallFeature id="callForwardBusy" text={TOOLTIPS_TEXT.CALL_FORWARD_BUSY}/>
                  </div>
                  <div className="cf-row3-mid">
                    <Toggle
                      value={getP(currentPort).busyOn}
                      onToggle={async (v) => {
                        const next = !v;
                        setP(currentPort, (s) => ({
                          ...s,
                          busyOn: next,
                          busyExpand: true,
                          busyRoute: next ? s.busyRoute : "voicemail",
                          busyPhone: next ? s.busyPhone : "",
                        }));
                        if (next) {
                          toast.info("Call Forward Busy toggled ON. Click Save to apply.");
                        } else {
                          const meta = portMetaByLabel.get(currentPort);
                          if (!meta) return;
                          const payload = { callForwardBusy: { enabled: false, route: "voicemail", phoneNumber: "" } };
                          try {
                            await withSaving(`${currentPort}:busy`, async () => {
                              await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, payload);
                              toast.success(`Call Forward Busy disabled for ${currentPort}`);
                            });
                          } catch (e) {}
                        }
                      }}
                    />
                  </div>
                  <div className="cf-row3-right">
                    <span><b>Enable</b> *90</span>
                    <span><b>Disable</b> *91</span>
                  </div>
                </div>

                {getP(currentPort).busyExpand && (
                  <div className="cf-inline-controls">
                    <label className="cf-radio-inline">
                      <input
                        type="radio"
                        checked={getP(currentPort).busyRoute === "voicemail"}
                        onChange={() => setP(currentPort, (s) => ({ ...s, busyRoute: "voicemail", busyPhone: "" }))}
                      />
                      Voicemail
                    </label>
                    <label className="cf-radio-inline">
                      <input
                        type="radio"
                        checked={getP(currentPort).busyRoute === "phone"}
                        onChange={() => setP(currentPort, (s) => ({ ...s, busyRoute: "phone" }))}
                      />
                      Phone Number:
                    </label>
                    {getP(currentPort).busyRoute === "phone" && (
                      <PhoneInput value={getP(currentPort).busyPhone} onChange={(val) => setP(currentPort, (s) => ({ ...s, busyPhone: val }))} />
                    )}
                    <button
                      className={cx("cf-btn inline-save", !canSaveBusy && "cf-btn-warn")}
                      disabled={!!saving[`${currentPort}:busy`]}
                      onClick={() => withSaving(`${currentPort}:busy`, handleSaveBusy)}
                    >
                      {saving[`${currentPort}:busy`] ? "Saving…" : "Save"}
                    </button>
                  </div>
                )}

                <div className="cf-row3">
                  <div className="cf-row3-left">
                    <span className={cx("cf-caret", getP(currentPort).naExpand && "open")} onClick={() => setP(currentPort, (s) => ({ ...s, naExpand: !s.naExpand }))} />
                    <span className="cf-label">Call Forward No-Answer</span>
                    <InfoPopupCallFeature id="callForwardNoAnswer" text={TOOLTIPS_TEXT.CALL_FORWARD_NO_ANSWER}/>
                  </div>
                  <div className="cf-row3-mid">
                    <Toggle
                      value={getP(currentPort).naOn}
                      onToggle={async (v) => {
                        const next = !v;
                        setP(currentPort, (s) => ({
                          ...s,
                          naOn: next,
                          naExpand: true,
                          naRoute: next ? s.naRoute : "voicemail",
                          naPhone: next ? s.naPhone : "",
                          naSeconds: next ? s.naSeconds : 6,
                        }));
                        if (next) {
                          toast.info("No-Answer forwarding toggled ON. Click Save to apply.");
                        } else {
                          const meta = portMetaByLabel.get(currentPort);
                          if (!meta) return;
                          const payload = { callForwardNoAnswer: { enabled: false, ringSeconds: 6, route: "voicemail", phoneNumber: "" } };
                          try {
                            await withSaving(`${currentPort}:noanswer`, async () => {
                              await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, payload);
                              toast.success(`No-Answer forwarding disabled for ${currentPort}`);
                            });
                          } catch (e) {}
                        }
                      }}
                    />
                  </div>
                  <div className="cf-row3-right">
                    <span><b>Enable</b> *92</span>
                    <span><b>Disable</b> *93</span>
                  </div>
                </div>

                {getP(currentPort).naExpand && (
                  <>
                    <div className="cf-inline">
                      <span>After ringing this line for</span>
                      <SecondsDropdown value={getP(currentPort).naSeconds} onChange={(n) => setP(currentPort, (s) => ({ ...s, naSeconds: n }))} />
                      <span>seconds, forward unanswered calls to:</span>
                    </div>
                    <div className="cf-inline-controls">
                      <label className="cf-radio-inline">
                        <input
                          type="radio"
                          checked={getP(currentPort).naRoute === "voicemail"}
                          onChange={() => setP(currentPort, (s) => ({ ...s, naRoute: "voicemail", naPhone: "" }))}
                        />
                        Voicemail
                      </label>
                      <label className="cf-radio-inline">
                        <input
                          type="radio"
                          checked={getP(currentPort).naRoute === "phone"}
                          onChange={() => setP(currentPort, (s) => ({ ...s, naRoute: "phone" }))}
                        />
                        Phone Number:
                      </label>
                      {getP(currentPort).naRoute === "phone" && (
                        <PhoneInput value={getP(currentPort).naPhone} onChange={(val) => setP(currentPort, (s) => ({ ...s, naPhone: val }))} />
                      )}
                      <button
                        className={cx("cf-btn inline-save", !canSaveNA && "cf-btn-warn")}
                        disabled={!!saving[`${currentPort}:noanswer`]}
                        onClick={() => withSaving(`${currentPort}:noanswer`, handleSaveNoAnswer)}
                      >
                        {saving[`${currentPort}:noanswer`] ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </>
                )}

                <div className="cf-row3">
                  <div className="cf-row3-left">
                    <span className={cx("cf-caret", getP(currentPort).alwaysExpand && "open")} onClick={() => setP(currentPort, (s) => ({ ...s, alwaysExpand: !s.alwaysExpand }))} />
                    <span className="cf-label">Call Forward Always</span>
                    <InfoPopupCallFeature id="callForwardAlways" text={TOOLTIPS_TEXT.CALL_FORWARD_ALWAYS}/>
                  </div>
                  <div className="cf-row3-mid">
                    <Toggle
                      value={getP(currentPort).alwaysOn}
                      onToggle={async (v) => {
                        const next = !v;
                        setP(currentPort, (s) => ({
                          ...s,
                          alwaysOn: next,
                          alwaysExpand: true,
                          alwaysRoute: next ? s.alwaysRoute : "voicemail",
                          alwaysPhone: next ? s.alwaysPhone : "",
                        }));
                        if (next) {
                          toast.info("Call Forward Always toggled ON. Click Save to apply.");
                        } else {
                          const meta = portMetaByLabel.get(currentPort);
                          if (!meta) return;
                          const payload = { callForwardAlways: { enabled: false, route: "voicemail", phoneNumber: "" } };
                          try {
                            await withSaving(`${currentPort}:always`, async () => {
                              await updatePortFeature(token, meta.obiId, meta.boxId, meta.boxPortNumber, payload);
                              toast.success(`Call Forward Always disabled for ${currentPort}`);
                            });
                          } catch (e) {}
                        }
                      }}
                    />
                  </div>
                  <div className="cf-row3-right">
                    <span><b>Enable</b> *72</span>
                    <span><b>Disable</b> *73</span>
                  </div>
                </div>

                {getP(currentPort).alwaysExpand && (
                  <div className="cf-inline-controls">
                    <label className="cf-radio-inline">
                      <input
                        type="radio"
                        checked={getP(currentPort).alwaysRoute === "voicemail"}
                        onChange={() => setP(currentPort, (s) => ({ ...s, alwaysRoute: "voicemail", alwaysPhone: "" }))}
                      />
                      Voicemail
                    </label>
                    <label className="cf-radio-inline">
                      <input
                        type="radio"
                        checked={getP(currentPort).alwaysRoute === "phone"}
                        onChange={() => setP(currentPort, (s) => ({ ...s, alwaysRoute: "phone" }))}
                      />
                      Phone Number:
                    </label>
                    {getP(currentPort).alwaysRoute === "phone" && (
                      <PhoneInput value={getP(currentPort).alwaysPhone} onChange={(val) => setP(currentPort, (s) => ({ ...s, alwaysPhone: val }))} />
                    )}
                    <button
                      className={cx("cf-btn inline-save", !canSaveAlways && "cf-btn-warn")}
                      disabled={!!saving[`${currentPort}:always`]}
                      onClick={() => withSaving(`${currentPort}:always`, handleSaveAlways)}
                    >
                      {saving[`${currentPort}:always`] ? "Saving…" : "Save"}
                    </button>
                  </div>
                )}

                <div className="cf-section-title">{currentPort} Star Codes</div>
                <div className="cf-row3">
                  <div className="cf-row3-left">
                    <span className="cf-spacer" /> <span className="cf-label">Do Not Disturb</span>
                    <InfoPopupCallFeature id="doNotDisturb" text={TOOLTIPS_TEXT.DO_NOT_DISTURB}/>
                  </div>
                  <div className="cf-row3-mid">
                    <Toggle
                      value={getP(currentPort).dndOn}
                      onToggle={async (v) => {
                        const next = !v;
                        setP(currentPort, (s) => ({ ...s, dndOn: next }));
                        try {
                          await saveSimpleToggle(currentPort, "doNotDisturb", next);
                        } catch (e) {}
                      }}
                    />
                  </div>
                  <div className="cf-row3-right">
                    <span><b>Enable</b> *78</span>
                    <span><b>Disable</b> *79</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="cf-footer">
          <button className="cf-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      {showBulkModal && (
        <BulkSelectPortsModal
          open={showBulkModal}
          ports={portsMeta.map(({ label }) => ({ label }))}
          defaultSelected={[currentPort]}
          lockedLabels={[currentPort]}
          onClose={() => setShowBulkModal(false)}
          onSave={async (selectedPortLabels) => {
            if (!isPortView) return setShowBulkModal(false);
            const s = getP(currentPort);
            const busyData = buildBusyPayload(s);
            const naData = buildNoAnswerPayload(s);
            const alwaysData = buildAlwaysPayload(s);
            const simpleData = { doNotDisturb: !!s.dndOn ,conferenceCall: !!s.threeWayOn};
            const updateData = { ...busyData, ...naData, ...alwaysData, ...simpleData};
            const selectedPorts = selectedPortLabels
              .map((label) => {
                const meta = portMetaByLabel.get(label);
                return {
                  obiId: meta?.obiId,
                  boxId: meta?.boxId,
                  portNumber: label,
                  boxPortNumber: meta?.boxPortNumber,
                  macAddress: meta?.macAddress,
                };
              })
              .filter((p) => p.obiId && p.boxId);
            try {
              const result = await bulkUpdateCallFeaturePorts(token, boxDetails._id, selectedPorts, updateData);
              setShowBulkModal(false);
              onClose();
              if (result?.errors?.length > 0) {
                toast.error(`Bulk update completed (some failed). Succeeded: ${result?.summary?.succeeded ?? 0}, Failed: ${result?.summary?.failed ?? 0}`);
              } else {
                toast.success(`All ${result?.summary?.succeeded ?? selectedPorts.length} selected port(s) updated successfully`);
              }
            } catch (e) {
              console.error("Bulk update failed:", e);
              setShowBulkModal(false);
              toast.error(e?.message || "Bulk update failed");
            }
          }}
        />
      )}
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.AppState.user,
  boxDetails: state.AppState.boxDetails.box,
});

const mapDispatchToProps = (dispatch) => ({
  getSalveboxOob: (token, bid) => dispatch(BoxesActions.getSalveboxOob(token, bid)),
  updateDeviceFeatures: (token, boxId, body) => dispatch(BoxesActions.updateDeviceFeatures(token, boxId, body)),
  updatePortFeature: (token, obiId, boxId, boxPortNumber, updateData) =>
    dispatch(BoxesActions.updatePortFeature(token, obiId, boxId, boxPortNumber, updateData)),
  bulkUpdateCallFeaturePorts: (token, boxId, selectedPorts, updateData) =>
    dispatch(BoxesActions.bulkUpdateCallFeaturePorts(token, boxId, selectedPorts, updateData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CallFeaturesModal);
